import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { addLocale } from "primereact/api";
import { getTools } from "../../../data/loca/tools";
import {
    deleteDeliveryVehicleUnavailability,
    getDeliveryVehicleOverview,
    saveDeliveryVehicleUnavailability,
    setDeliveryVehicle
} from "../../../data/loca/deliveryVehicle";
import { notify, send } from "../../../services/NotificationService";
import { formatShortTime, formatVehicleDateTime, formatVehiclePeriod, legLabel } from "../rental/DeliveryVehicleNotice";

// Véhicule de livraison : le camion qui sert aux livraisons, reprises et installations est parfois indisponible
// (garage, loué à un client, usage personnel…). Le loueur saisit ces périodes ici ; les passages prévus pendant
// une indisponibilité sont signalés ici, dans le formulaire de location et sur la fiche location.

addLocale("fr", {
    firstDayOfWeek: 1,
    dayNames: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
    dayNamesShort: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
    dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
    monthNames: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
    monthNamesShort: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
    today: "Aujourd'hui",
    clear: "Effacer",
    chooseTime: "Choisir l'heure",
    chooseDate: "Choisir la date"
});

const REASON_ICONS = {
    maintenance: "pi-wrench",
    rented: "pi-key",
    personal: "pi-user",
    other: "pi-ban"
};

const WEEKDAYS = ["Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam.", "Dim."];

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const sameDay = (a, b) => a.toDateString() === b.toDateString();
const dayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

// Grille du mois : du lundi précédant le 1er au dimanche suivant le dernier jour.
function getMonthGrid(month) {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const gridStart = addDays(first, -((first.getDay() + 6) % 7));
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const gridEnd = addDays(last, 7 - ((last.getDay() + 6) % 7));
    const weeks = [];
    for (let day = gridStart; day < gridEnd; day = addDays(day, 7)) {
        weeks.push(Array.from({ length: 7 }, (_, index) => addDays(day, index)));
    }
    return { gridStart, gridEnd, weeks };
}

const parseBlock = (block) => ({ ...block, startDate: new Date(block.start), endDate: new Date(block.end) });
const parseLeg = (leg) => ({ ...leg, anchorDate: new Date(leg.anchor) });

const blockClass = (block) => block.source === "rental" ? "is-rental" : `is-${block.reason || "other"}`;

// Couloirs d'affichage : deux périodes qui se chevauchent ne partagent pas la même ligne dans le calendrier.
function assignLanes(blocks) {
    const laneEnds = [];
    return blocks
        .slice()
        .sort((a, b) => a.startDate - b.startDate || b.endDate - a.endDate)
        .map((block) => {
            let lane = laneEnds.findIndex((end) => end <= block.startDate);
            if (lane === -1) {
                lane = laneEnds.length;
                laneEnds.push(block.endDate);
            } else {
                laneEnds[lane] = block.endDate;
            }
            return { ...block, lane };
        });
}

export async function loader() {
    const month = startOfDay(new Date());
    month.setDate(1);
    const { gridStart, gridEnd } = getMonthGrid(month);
    const [overview, tools] = await Promise.all([getDeliveryVehicleOverview(gridStart, gridEnd), getTools()]);
    return { overview, tools: tools ?? [], month };
}

const pad2 = (value) => String(value).padStart(2, "0");
const toTimeValue = (date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

// Raccourcis d'horaires de la fenêtre de saisie.
const TIME_PRESETS = [
    { key: "morning", label: "Matin", start: "08:00", end: "12:00" },
    { key: "afternoon", label: "Après-midi", start: "13:00", end: "18:00" },
    { key: "day", label: "Journée", start: "08:00", end: "18:00" }
];

// « 08:00 » → « 8h », « 13:30 » → « 13h30 ».
const presetTime = (time) => time.replace(/^0/, "").replace(":00", "h").replace(":", "h");

// Formulaire : jour + heure de début, jour + heure de fin ; « 24 h/24 » = de minuit le premier jour à minuit après le dernier.
function emptyForm(day) {
    const start = startOfDay(day ?? new Date());
    return { id: null, reason: "maintenance", allDay: false, startDay: start, startTime: "08:00", endDay: start, endTime: "18:00", note: "" };
}

function formFromBlock(block) {
    const start = new Date(block.start);
    const end = new Date(block.end);
    const allDay = start.getHours() === 0 && start.getMinutes() === 0 && end.getHours() === 0 && end.getMinutes() === 0;
    return {
        id: block.id,
        reason: block.reason || "other",
        allDay,
        startDay: startOfDay(start),
        startTime: allDay ? "08:00" : toTimeValue(start),
        endDay: allDay ? startOfDay(addDays(end, -1)) : startOfDay(end),
        endTime: allDay ? "18:00" : toTimeValue(end),
        note: block.note || ""
    };
}

const withTime = (day, time) => {
    const [hours, minutes] = String(time || "").split(":").map(Number);
    if (!day || Number.isNaN(hours)) return null;
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, Number.isNaN(minutes) ? 0 : minutes);
};

// Période effectivement enregistrée.
function formPeriod(form) {
    if (!form.startDay || !form.endDay) return null;
    if (form.allDay) {
        return { start: startOfDay(form.startDay), end: addDays(startOfDay(form.endDay), 1) };
    }
    const start = withTime(form.startDay, form.startTime);
    const end = withTime(form.endDay, form.endTime);
    return start && end ? { start, end } : null;
}

function UnavailabilityDialog({ visible, form, setForm, reasons, onHide, onSaved, onDelete }) {
    const [isSaving, setIsSaving] = useState(false);
    const period = formPeriod(form);
    const periodError = period && period.end <= period.start ? "La fin doit être après le début." : null;

    const save = async () => {
        if (!period) {
            send("warning", form.allDay ? "Choisissez au moins un jour." : "Renseignez le début et la fin.");
            notify();
            return;
        }
        if (periodError) {
            send("warning", periodError);
            notify();
            return;
        }
        setIsSaving(true);
        try {
            const result = await saveDeliveryVehicleUnavailability(form.id, { ...period, reason: form.reason, note: form.note.trim() });
            const conflicts = result?.conflicts ?? [];
            send(conflicts.length ? "warning" : "success", conflicts.length
                ? `Indisponibilité enregistrée. Attention : ${conflicts.length} ${conflicts.length > 1 ? "passages prévus tombent" : "passage prévu tombe"} pendant cette période.`
                : "Indisponibilité enregistrée.");
            onSaved(result?.unavailability);
            onHide();
        } catch (error) {
            send("error", error?.message || "Enregistrement impossible.");
        } finally {
            setIsSaving(false);
            notify();
        }
    };

    return (
        <Dialog
            header={form.id ? "Modifier l'indisponibilité" : "Nouvelle indisponibilité"}
            visible={visible}
            onHide={onHide}
            className="dv-dialog"
            style={{ width: "min(540px, 94vw)" }}
            footer={(
                <div className="dv-dialog__footer">
                    {form.id
                        ? <Button label="Supprimer" icon="pi pi-trash" severity="danger" text onClick={(event) => onDelete(event, form.id)} disabled={isSaving} />
                        : <span />}
                    <div>
                        <Button label="Annuler" severity="secondary" text onClick={onHide} disabled={isSaving} />
                        <Button label="Enregistrer" icon="pi pi-check" onClick={save} loading={isSaving} disabled={isSaving} data-cy="dvSave" />
                    </div>
                </div>
            )}
        >
            <div className="dv-field">
                <span className="dv-field__label">Motif</span>
                <div className="dv-reasons" role="radiogroup" aria-label="Motif">
                    {reasons.map((reason) => (
                        <button
                            type="button"
                            key={reason.key}
                            role="radio"
                            aria-checked={form.reason === reason.key}
                            className={`dv-reason is-${reason.key}${form.reason === reason.key ? " is-selected" : ""}`}
                            onClick={() => setForm({ ...form, reason: reason.key })}
                        >
                            <i className={`pi ${REASON_ICONS[reason.key] ?? "pi-ban"}`} aria-hidden="true"></i>
                            <span>{reason.key === "other" ? "Autre" : reason.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="dv-when">
                <span className="dv-field__label" id="dv-start-label">Début</span>
                <div className="dv-when__row" role="group" aria-labelledby="dv-start-label">
                    <Calendar inputId="dv-start-day" value={form.startDay} locale="fr" dateFormat="dd/mm/yy" showIcon readOnlyInput
                        aria-label="Jour de début"
                        onChange={(e) => {
                            const startDay = e.value;
                            // Fin ramenée au même jour si elle tombait avant le nouveau début.
                            const endDay = startDay && form.endDay && form.endDay < startDay ? startDay : form.endDay;
                            setForm({ ...form, startDay, endDay });
                        }} />
                    {!form.allDay && (
                        <input type="time" id="dv-start-time" className="p-inputtext p-component dv-time" step={900} value={form.startTime}
                            aria-label="Heure de début" onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                    )}
                </div>

                <span className="dv-field__label" id="dv-end-label">Fin</span>
                <div className="dv-when__row" role="group" aria-labelledby="dv-end-label">
                    <Calendar inputId="dv-end-day" value={form.endDay} locale="fr" dateFormat="dd/mm/yy" showIcon readOnlyInput
                        aria-label="Jour de fin" minDate={form.startDay ?? undefined}
                        onChange={(e) => setForm({ ...form, endDay: e.value })} />
                    {!form.allDay && (
                        <input type="time" id="dv-end-time" className="p-inputtext p-component dv-time" step={900} value={form.endTime}
                            aria-label="Heure de fin" onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                    )}
                </div>

                <div className="dv-presets" aria-label="Raccourcis d'horaires">
                    {TIME_PRESETS.map((preset) => {
                        const selected = !form.allDay && form.startTime === preset.start && form.endTime === preset.end;
                        return (
                            <button type="button" key={preset.key} className={`dv-preset${selected ? " is-selected" : ""}`}
                                onClick={() => setForm({ ...form, allDay: false, startTime: preset.start, endTime: preset.end })}>
                                {preset.label} <small>{presetTime(preset.start)}–{presetTime(preset.end)}</small>
                            </button>
                        );
                    })}
                    <button type="button" className={`dv-preset${form.allDay ? " is-selected" : ""}`}
                        onClick={() => setForm({ ...form, allDay: !form.allDay })}>
                        24 h/24 <small>jours entiers</small>
                    </button>
                </div>
            </div>

            <div className="dv-field">
                <label className="dv-field__label" htmlFor="dv-note">Précision <small>(facultatif)</small></label>
                <InputText id="dv-note" value={form.note} maxLength={300} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Ex. contrôle technique, pneus, prêté à un ami…" className="w-full" />
            </div>

            {period && !periodError && (
                <p className="dv-dialog__summary">
                    <i className="pi pi-calendar" aria-hidden="true"></i>
                    Indisponible : <b>{formatVehiclePeriod(period.start, period.end)}</b>
                </p>
            )}
            {periodError && <p className="dv-dialog__summary is-error"><i className="pi pi-exclamation-triangle" aria-hidden="true"></i> {periodError}</p>}
        </Dialog>
    );
}

// Horaires affichés devant le motif dans une case : « 14h30–17h45 », « dès 14h30 », « → 12h ».
function timePrefix(block, partialStart, partialEndSameCell) {
    if (partialStart && partialEndSameCell) return `${formatShortTime(block.startDate)}–${formatShortTime(block.endDate)} · `;
    if (partialStart) return `dès ${formatShortTime(block.startDate)} · `;
    if (partialEndSameCell) return `→ ${formatShortTime(block.endDate)} · `;
    return "";
}

function DayCell({ day, month, week, lanes, blocks, legs, onAdd, onEditBlock }) {
    const navigate = useNavigate();
    const isToday = sameDay(day, new Date());
    const outside = day.getMonth() !== month.getMonth();
    const dayStart = day;
    const dayEnd = addDays(day, 1);
    const weekStart = week[0];
    const dayBlocks = blocks.filter((block) => block.startDate < dayEnd && block.endDate > dayStart);
    const visibleLegs = legs.slice(0, 3);

    return (
        <div className={`dv-day${outside ? " is-outside" : ""}${isToday ? " is-today" : ""}`}
            onClick={() => onAdd(day)} role="gridcell" aria-label={day.toLocaleDateString("fr-FR", { dateStyle: "full" })}>
            <span className="dv-day__number">{day.getDate()}</span>
            <div className="dv-day__lanes">
                {Array.from({ length: lanes }, (_, lane) => {
                    const block = dayBlocks.find((item) => item.lane === lane);
                    if (!block) return <span key={lane} className="dv-bar dv-bar--empty" aria-hidden="true" />;
                    const startsHere = block.startDate >= dayStart;
                    const endsHere = block.endDate <= dayEnd;
                    const showLabel = startsHere || sameDay(day, weekStart);
                    const visibleEnd = block.endDate < addDays(weekStart, 7) ? block.endDate : addDays(weekStart, 7);
                    const spanDays = Math.max(1, Math.ceil((visibleEnd - dayStart) / (24 * 60 * 60 * 1000)));
                    const partialStart = startsHere && block.startDate > dayStart;
                    const partialEnd = endsHere && block.endDate < dayEnd;
                    const title = `${block.label} · ${formatVehiclePeriod(block.rentalStart ?? block.start, block.rentalEnd ?? block.end)}${block.note ? ` — ${block.note}` : ""}`;
                    return (
                        <button
                            type="button"
                            key={lane}
                            title={title}
                            className={`dv-bar ${blockClass(block)}${startsHere ? " is-start" : ""}${endsHere ? " is-end" : ""}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (block.source === "rental") navigate(`/${block.rentalPublicId}`);
                                else onEditBlock(block);
                            }}
                        >
                            {showLabel && (
                                <span style={{ "--dv-span": spanDays }}>
                                    {timePrefix(block, partialStart, partialEnd && startsHere)}
                                    {block.label}
                                </span>
                            )}
                            {!showLabel && partialEnd && <span>→ {formatShortTime(block.endDate)}</span>}
                        </button>
                    );
                })}
            </div>
            {visibleLegs.map((leg) => (
                <Link
                    key={`${leg.rentalPublicId}-${leg.kind}`}
                    to={`/${leg.rentalPublicId}`}
                    onClick={(event) => event.stopPropagation()}
                    className={`dv-leg${leg.conflicts.length ? " is-conflict" : ""}`}
                    title={`${legLabel(leg)} · ${leg.clientName}${leg.materialNames?.length ? ` · ${leg.materialNames.join(", ")}` : ""}${leg.conflicts.length ? " — véhicule indisponible" : ""}`}
                >
                    <i className={`pi ${leg.conflicts.length ? "pi-exclamation-triangle" : leg.kind === "delivery" ? "pi-sign-out" : "pi-sign-in"}`} aria-hidden="true"></i>
                    <span>{formatShortTime(leg.anchorDate)} {leg.kind === "delivery" ? "Livr." : "Rep."} {leg.clientName}</span>
                </Link>
            ))}
            {legs.length > visibleLegs.length && <span className="dv-day__more">+{legs.length - visibleLegs.length}</span>}
        </div>
    );
}

export default function DeliveryVehiclePage() {
    const loaded = useLoaderData();
    const [month, setMonth] = useState(loaded.month);
    const [overview, setOverview] = useState(loaded.overview);
    const [tools] = useState(loaded.tools);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingVehicle, setIsSavingVehicle] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [form, setForm] = useState(emptyForm());

    const grid = useMemo(() => getMonthGrid(month), [month]);

    const reload = useCallback(async (targetMonth = month) => {
        const { gridStart, gridEnd } = getMonthGrid(targetMonth);
        setIsLoading(true);
        try {
            const next = await getDeliveryVehicleOverview(gridStart, gridEnd);
            if (next) setOverview(next);
        } catch (error) {
            send("error", error?.message || "Chargement du planning impossible.");
            notify();
        } finally {
            setIsLoading(false);
        }
    }, [month]);

    const goToMonth = (offset) => {
        const next = offset === 0
            ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            : new Date(month.getFullYear(), month.getMonth() + offset, 1);
        setMonth(next);
        reload(next);
    };

    // Raccourci clavier : flèches gauche/droite pour changer de mois quand aucune fenêtre n'est ouverte.
    useEffect(() => {
        const onKey = (event) => {
            if (dialogVisible || ["INPUT", "TEXTAREA"].includes(event.target?.tagName)) return;
            if (event.key === "ArrowLeft") goToMonth(-1);
            if (event.key === "ArrowRight") goToMonth(1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    const reasons = overview?.reasons ?? [];
    const blocks = useMemo(() => assignLanes((overview?.blocks ?? []).map(parseBlock)), [overview]);
    const legs = useMemo(() => (overview?.legs ?? []).map(parseLeg), [overview]);
    const legsByDay = useMemo(() => {
        const map = new Map();
        legs.forEach((leg) => {
            const key = dayKey(leg.anchorDate);
            map.set(key, [...(map.get(key) ?? []), leg]);
        });
        return map;
    }, [legs]);
    const upcomingBlocks = (overview?.upcoming?.blocks ?? []).map(parseBlock);
    const upcomingConflicts = (overview?.upcoming?.conflicts ?? []).map(parseLeg);
    const monthConflicts = legs.filter((leg) => leg.conflicts.length > 0 && leg.anchorDate.getMonth() === month.getMonth()).length;
    const vehicle = overview?.vehicle ?? null;
    const legMinutes = overview?.legMinutes;

    const toolOptions = useMemo(() => [
        { value: null, label: "Aucune annonce (indisponibilités saisies uniquement)" },
        ...tools
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((tool) => ({ value: tool.publicId, label: tool.name }))
    ], [tools]);

    const openNew = (day) => {
        setForm(emptyForm(day ?? new Date()));
        setDialogVisible(true);
    };

    const openEdit = (block) => {
        setForm(formFromBlock(block));
        setDialogVisible(true);
    };

    const confirmDelete = (event, id) => {
        confirmPopup({
            target: event.currentTarget,
            message: "Supprimer cette indisponibilité ?",
            icon: "pi pi-trash",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            acceptLabel: "Supprimer",
            rejectLabel: "Annuler",
            accept: async () => {
                try {
                    await deleteDeliveryVehicleUnavailability(id);
                    send("success", "Indisponibilité supprimée.");
                    setDialogVisible(false);
                    await reload();
                } catch (error) {
                    send("error", error?.message || "Suppression impossible.");
                }
                notify();
            }
        });
    };

    const changeVehicle = async (toolPublicId) => {
        setIsSavingVehicle(true);
        try {
            await setDeliveryVehicle(toolPublicId);
            send("success", toolPublicId ? "Annonce du véhicule enregistrée : ses locations bloquent désormais les livraisons." : "Plus aucune annonce rattachée au véhicule.");
            await reload();
        } catch (error) {
            send("error", error?.message || "Enregistrement impossible.");
        } finally {
            setIsSavingVehicle(false);
            notify();
        }
    };

    const monthTitle = month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    // Agenda (petits écrans) : une période n'apparaît qu'une fois, à son premier jour visible dans le mois.
    const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
    const agendaBlocksByDay = new Map();
    blocks.forEach((block) => {
        const firstDay = block.startDate < monthStart ? monthStart : startOfDay(block.startDate);
        if (firstDay.getMonth() !== month.getMonth() || firstDay.getFullYear() !== month.getFullYear()) return;
        agendaBlocksByDay.set(dayKey(firstDay), [...(agendaBlocksByDay.get(dayKey(firstDay)) ?? []), block]);
    });
    const agendaDays = grid.weeks.flat().filter((day) => day.getMonth() === month.getMonth()
        && (legsByDay.has(dayKey(day)) || agendaBlocksByDay.has(dayKey(day))));
    const agendaDayLabel = (day) => {
        const label = day.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
        return label.charAt(0).toUpperCase() + label.slice(1);
    };

    return (
        <div className="lk-page dv-page">
            <ConfirmPopup />
            <header className="lk-page-header">
                <div className="lk-page-header__title">
                    <span className="lk-page-header__icon"><i className="pi pi-truck" aria-hidden="true"></i></span>
                    <div>
                        <p className="lk-kicker">Outils</p>
                        <h1>Véhicule de livraison</h1>
                        <p className="lk-page-header__subtitle">
                            Indiquez quand {vehicle ? `« ${vehicle.name} »` : "votre camion"} n'est pas disponible : les livraisons, reprises et
                            installations prévues à ces moments-là sont signalées, ici et dans le formulaire de location.
                        </p>
                    </div>
                </div>
                <div className="lk-page-header__actions">
                    <Button icon="pi pi-plus" label="Ajouter une indisponibilité" onClick={() => openNew()} data-cy="dvAdd" />
                </div>
            </header>

            {upcomingConflicts.length > 0 && (
                <section className="dv-alert" role="alert">
                    <i className="pi pi-exclamation-triangle" aria-hidden="true"></i>
                    <div>
                        <strong>
                            {upcomingConflicts.length} {upcomingConflicts.length > 1 ? "passages à venir tombent" : "passage à venir tombe"} pendant une indisponibilité du véhicule
                        </strong>
                        <ul>
                            {upcomingConflicts.map((leg) => (
                                <li key={`${leg.rentalPublicId}-${leg.kind}`}>
                                    <Link to={`/${leg.rentalPublicId}`}>{legLabel(leg)} du {formatVehicleDateTime(leg.anchorDate)} · {leg.clientName}</Link>
                                    <span> — {leg.conflicts.map((block) => block.label).join(", ")}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            <div className="dv-layout">
                <section className="lk-card dv-calendar" aria-busy={isLoading}>
                    <div className="dv-calendar__toolbar">
                        <div className="dv-calendar__nav">
                            <Button icon="pi pi-chevron-left" rounded text aria-label="Mois précédent" onClick={() => goToMonth(-1)} />
                            <h2>{monthTitle}</h2>
                            <Button icon="pi pi-chevron-right" rounded text aria-label="Mois suivant" onClick={() => goToMonth(1)} />
                            <Button label="Aujourd'hui" size="small" outlined onClick={() => goToMonth(0)} />
                            {isLoading && <i className="pi pi-spin pi-spinner dv-calendar__spinner" aria-hidden="true"></i>}
                        </div>
                        {monthConflicts > 0 && (
                            <span className="dv-chip is-danger"><i className="pi pi-exclamation-triangle" aria-hidden="true"></i> {monthConflicts} en conflit ce mois-ci</span>
                        )}
                    </div>

                    <div className="dv-legend" aria-label="Légende">
                        {reasons.map((reason) => (
                            <span key={reason.key} className={`dv-legend__item is-${reason.key}`}>{reason.key === "other" ? "Autre" : reason.label}</span>
                        ))}
                        {vehicle && <span className="dv-legend__item is-rental">Location de l'annonce</span>}
                        <span className="dv-legend__item is-leg">Livraison / reprise</span>
                        <span className="dv-legend__item is-conflict">Passage en conflit</span>
                    </div>

                    <div className="dv-grid" role="grid" aria-label={`Planning du véhicule, ${monthTitle}`}>
                        {WEEKDAYS.map((weekday) => <div key={weekday} className="dv-grid__weekday" role="columnheader">{weekday}</div>)}
                        {grid.weeks.map((week) => {
                            const weekEnd = addDays(week[0], 7);
                            const weekBlocks = blocks.filter((block) => block.startDate < weekEnd && block.endDate > week[0]);
                            const lanes = weekBlocks.length ? Math.max(...weekBlocks.map((block) => block.lane)) + 1 : 0;
                            return week.map((day) => (
                                <DayCell key={dayKey(day)} day={day} month={month} week={week} lanes={lanes} blocks={weekBlocks}
                                    legs={legsByDay.get(dayKey(day)) ?? []} onAdd={openNew} onEditBlock={openEdit} />
                            ));
                        })}
                    </div>

                    {/* Petits écrans : liste des jours du mois qui ont quelque chose. */}
                    <div className="dv-agenda">
                        {agendaDays.length === 0 && <p className="dv-empty">Rien de prévu ce mois-ci : le véhicule est disponible.</p>}
                        {agendaDays.map((day) => {
                            const dayBlocks = agendaBlocksByDay.get(dayKey(day)) ?? [];
                            const dayLegs = legsByDay.get(dayKey(day)) ?? [];
                            return (
                                <div key={dayKey(day)} className={`dv-agenda__day${sameDay(day, new Date()) ? " is-today" : ""}`}>
                                    <span className="dv-agenda__date">{agendaDayLabel(day)}</span>
                                    <div>
                                        {dayBlocks.map((block) => (
                                            <button type="button" key={`${block.source}-${block.id ?? block.rentalPublicId}`} className={`dv-bar is-start is-end ${blockClass(block)}`}
                                                onClick={() => (block.source === "rental" ? null : openEdit(block))}>
                                                <span>{block.label} · {formatVehiclePeriod(block.rentalStart ?? block.start, block.rentalEnd ?? block.end)}</span>
                                            </button>
                                        ))}
                                        {dayLegs.map((leg) => (
                                            <Link key={`${leg.rentalPublicId}-${leg.kind}`} to={`/${leg.rentalPublicId}`} className={`dv-leg${leg.conflicts.length ? " is-conflict" : ""}`}>
                                                <i className={`pi ${leg.conflicts.length ? "pi-exclamation-triangle" : "pi-truck"}`} aria-hidden="true"></i>
                                                <span>{formatShortTime(leg.anchorDate)} {legLabel(leg)} · {leg.clientName}</span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="dv-calendar__hint">Cliquez sur un jour pour ajouter une indisponibilité, sur une période pour la modifier.</p>
                </section>

                <aside className="dv-side">
                    <div className="dv-side__main">
                        <section className="lk-card dv-vehicle">
                            <h2 className="lk-card__title">Votre véhicule</h2>
                            <p className="lk-card__subtitle">Si votre camion est aussi proposé à la location, choisissez son annonce : ses réservations le rendront indisponible automatiquement.</p>
                            <label className="dv-field__label" htmlFor="dv-vehicle-tool">Annonce du véhicule</label>
                            <Dropdown inputId="dv-vehicle-tool" value={vehicle?.toolPublicId ?? null} options={toolOptions} optionLabel="label" optionValue="value"
                                onChange={(e) => changeVehicle(e.value)} filter filterPlaceholder="Rechercher une annonce" disabled={isSavingVehicle}
                                placeholder="Aucune annonce" className="w-full" />
                            {vehicle && (
                                <p className="dv-vehicle__status">
                                    <i className="pi pi-link" aria-hidden="true"></i>
                                    <span>
                                        Les locations de <Link to={`/monoutil/${vehicle.toolPublicId}/details`}>{vehicle.name}</Link> bloquent le véhicule,
                                        battements avant / après compris.{!vehicle.isActive && " L'annonce est désactivée."}
                                    </span>
                                </p>
                            )}
                        </section>
    
                        {legMinutes && (
                            <section className="dv-help">
                                <h3><i className="pi pi-info-circle" aria-hidden="true"></i> Quand y a-t-il conflit ?</h3>
                                <p>
                                    Un passage mobilise le véhicule autour de l'heure prévue : {legMinutes.delivery} min avant et après pour une livraison,
                                    {" "}{legMinutes.pickup} min pour une reprise, {legMinutes.deliveryWithSetup} min avec installation, plus le trajet s'il a été estimé.
                                    Ces durées se règlent dans <Link to="/parametres">Paramètres</Link> (événements d'agenda).
                                </p>
                            </section>
                        )}
                    </div>

                    <section className="lk-card dv-upcoming">
                        <div className="lk-card__header">
                            <h2 className="lk-card__title">Prochaines indisponibilités</h2>
                            <Button icon="pi pi-plus" rounded text aria-label="Ajouter une indisponibilité" onClick={() => openNew()} />
                        </div>
                        {upcomingBlocks.length === 0 ? (
                            <p className="dv-empty">Aucune indisponibilité prévue dans les 6 prochains mois.</p>
                        ) : (
                            <ul className="dv-list">
                                {upcomingBlocks.map((block) => (
                                    <li key={`${block.source}-${block.id ?? block.rentalPublicId}`} className={`dv-list__item ${blockClass(block)}`}>
                                        <span className="dv-list__icon"><i className={`pi ${block.source === "rental" ? "pi-calendar" : REASON_ICONS[block.reason] ?? "pi-ban"}`} aria-hidden="true"></i></span>
                                        <div className="dv-list__text">
                                            {block.source === "rental"
                                                ? <Link to={`/${block.rentalPublicId}`}><b>{block.label}</b></Link>
                                                : <b>{block.label}</b>}
                                            <span>{formatVehiclePeriod(block.rentalStart ?? block.start, block.rentalEnd ?? block.end)}</span>
                                            {block.note && <em>{block.note}</em>}
                                        </div>
                                        {block.source !== "rental" && (
                                            <div className="dv-list__actions">
                                                <Button icon="pi pi-pencil" rounded text aria-label="Modifier" onClick={() => openEdit(block)} />
                                                <Button icon="pi pi-trash" rounded text severity="danger" aria-label="Supprimer" onClick={(event) => confirmDelete(event, block.id)} />
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </aside>
            </div>

            <UnavailabilityDialog
                visible={dialogVisible}
                form={form}
                setForm={setForm}
                reasons={reasons}
                onHide={() => setDialogVisible(false)}
                onSaved={() => reload()}
                onDelete={confirmDelete}
            />
        </div>
    );
}
